import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';

const PrivacyStatementPage: React.FC = () => {
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const navigate = useNavigate();

  const handleAccept = () => {
    setPrivacyAccepted(true);
    // Navigate to signup page after accepting privacy policy
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-16">
        <Helmet>
          <title>Privacy Statement - Valuefy</title>
          <meta name="description" content="Valuefy's Privacy Statement - Learn how we protect and handle your data" />
        </Helmet>

        <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">
          Privacy Statement
        </h1>
        <p className="text-gray-300 mb-8">Effective Date: 07 May 2025</p>

        <div className="space-y-8">
          <section>
            <p className="text-gray-300 mb-6">
              Valuefy Technologies Private Limited ("Valuefy", "we", "our", or "us") is committed to protecting the privacy and security of the information we collect and process. As a provider of Software as a Service (SaaS) and software development solutions for wealth and portfolio management, we uphold high standards of data protection aligned with international frameworks such as ISO 27001 and SOC 2.
            </p>

            <p className="text-gray-300 mb-6">
              By using our website, platform, and services ("Services"), you acknowledge that you have read and understood this Privacy Statement and agree to the practices described herein. If you do not agree with this policy, please do not use our Services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">1. Information We Collect</h2>
            <p className="text-gray-300 mb-4">We may collect the following categories of information, depending on how you interact with us:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-300">
              <li>Personal identifiers, such as your name, email address, phone number, job title, and company name</li>
              <li>Financial and portfolio-related data, including investment preferences, account details, transaction history, and performance metrics (when applicable through our services)</li>
              <li>Usage and technical information such as IP address, browser and device type, access times, error logs, referring URLs, and navigation activity on our website or platform</li>
              <li>Information collected through cookies and similar technologies to understand user behavior, personalize content, and improve system performance</li>
              <li>Voluntarily provided information, such as when you contact us, fill out forms, respond to surveys, or participate in webinars or events</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">2. How We Use Your Information</h2>
            <p className="text-gray-300 mb-4">We use the information we collect for the following purposes:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-300">
              <li>To deliver, operate, and maintain our software and services</li>
              <li>To personalize user experiences and improve content relevance</li>
              <li>To provide customer support, address inquiries, and manage user accounts</li>
              <li>To send updates, notifications, and service-related communications</li>
              <li>To analyze usage trends, improve system performance, and enhance features</li>
              <li>To comply with legal, regulatory, or contractual obligations</li>
              <li>To prevent fraud, abuse, and violations of our Terms of Use or applicable laws</li>
              <li>For other business functions aligned with your relationship with us or as disclosed at the time of data collection</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">3. Legal Basis for Processing</h2>
            <p className="text-gray-300 mb-4">Where required by law, we process your personal information based on one or more of the following legal grounds:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-300">
              <li>Your consent</li>
              <li>The necessity of the processing for a contract with you</li>
              <li>Compliance with legal obligations</li>
              <li>Our legitimate interests in delivering and improving our services</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">4. Cookies and Tracking Technologies</h2>
            <p className="text-gray-300 mb-6">
              We use cookies and similar technologies to collect certain technical information automatically when you access our Services. These tools help us enhance your experience, understand usage behavior, and improve performance.
            </p>
            <p className="text-gray-300 mb-6">
              You may manage or disable cookies through your browser settings. However, please note that certain features of the Services may not function properly without them.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">5. Data Sharing and Disclosure</h2>
            <p className="text-gray-300 mb-4">We do not sell or rent your personal information. We may share data only in the following circumstances:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-300">
              <li>With trusted service providers who help us operate our platform, perform analytics, host data, and provide support services under confidentiality obligations</li>
              <li>As required by law, regulation, legal process, or government request</li>
              <li>To protect our rights, security, and the safety of our users and systems</li>
              <li>In connection with a business transaction such as a merger, acquisition, reorganization, or asset sale, where such disclosure is part of the transaction</li>
              <li>With your explicit consent or at your direction</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">6. International Data Transfers</h2>
            <p className="text-gray-300 mb-6">
              Your data may be transferred to and processed in countries outside of your jurisdiction, including countries that may not offer the same level of data protection. In such cases, we implement appropriate safeguards such as Standard Contractual Clauses (SCCs) or rely on adequacy decisions as required under applicable laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">7. Data Security</h2>
            <p className="text-gray-300 mb-4">We maintain robust administrative, technical, and physical safeguards to protect personal information. Our information security program is aligned with industry standards, including ISO 27001 and SOC 2. Measures include:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-300">
              <li>Encryption of data in transit and at rest</li>
              <li>Role-based access controls</li>
              <li>Periodic audits, vulnerability assessments, and penetration testing</li>
              <li>Employee awareness and training programs</li>
              <li>Secure software development and change management protocols</li>
            </ul>
            <p className="text-gray-300 mb-6">
              Despite our efforts, no security system is completely foolproof. We urge you to take appropriate precautions and understand that information shared with us is at your own risk.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">8. Data Retention</h2>
            <p className="text-gray-300 mb-6">
              We retain personal data for as long as necessary to fulfill the purposes outlined in this statement, unless a longer retention period is required or permitted by law. When no longer needed, we securely delete or anonymize data in accordance with our retention policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">9. Your Rights</h2>
            <p className="text-gray-300 mb-4">Depending on your location and applicable laws (such as the GDPR, UK DPA, or other data protection laws), you may have the right to:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-300">
              <li>Access the personal data we hold about you</li>
              <li>Request correction or deletion of inaccurate or outdated information</li>
              <li>Object to or restrict certain types of processing</li>
              <li>Withdraw consent, where processing is based on consent</li>
              <li>Request a copy of your data in a portable format</li>
              <li>Lodge a complaint with a supervisory authority if you believe your rights have been violated</li>
            </ul>
            <p className="text-gray-300 mb-6">
              To exercise your rights or raise concerns about our privacy practices, please contact us using the information below.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">10. Third-Party Sites and Services</h2>
            <p className="text-gray-300 mb-6">
              Our Services may contain links to external websites or third-party services. This Privacy Statement does not apply to those websites, and we are not responsible for their content or privacy practices. We encourage you to review their privacy policies before interacting with them.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">11. Customer Data in Our Platform</h2>
            <p className="text-gray-300 mb-6">
              In our role as a service provider, customers may submit data to our platform for processing purposes. We process such customer data in accordance with the terms of our service agreements and only as instructed by the customer. We do not access, use, or disclose customer-submitted data except to provide the service or comply with legal obligations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">12. Changes to This Privacy Statement</h2>
            <p className="text-gray-300 mb-6">
              We may update this Privacy Statement periodically to reflect changes in our practices, technologies, or legal requirements. Updates will be posted on this page with a revised effective date. We encourage you to review this page regularly. Continued use of the Services after an update constitutes your acceptance of the changes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">13. Contact Us</h2>
            <p className="text-gray-300 mb-6">
              If you have any questions, requests, or complaints related to this Privacy Statement or our handling of your personal data, please contact us at:
            </p>
            <p className="text-gray-300 mb-6">
              Email: <a href="mailto:biz@valuefy.com" className="text-purple-400 hover:text-purple-300">biz@valuefy.com</a>
            </p>
          </section>
        </div>

        <div className="mt-12 p-6 bg-[rgba(69,42,124,0.10)] rounded-lg">
          <p className="text-gray-300 mb-4">
            <strong>Privacy Policy Acceptance</strong> - By clicking "Accept", you acknowledge that you have read and understood our Privacy Policy and agree to the collection, use, and disclosure of your information as described herein.
          </p>
          <div className="flex flex-wrap gap-4">
            <button 
              onClick={handleAccept}
              className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-2 rounded-md transition-colors"
            >
              Accept
            </button>
          </div>
          <div className="mt-4 text-sm text-gray-400">
            Status: {privacyAccepted ? 'Accepted' : 'Not Accepted'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyStatementPage; 